import { useEffect, useState } from "react"

import {
  getCityShipmentData,
  getStateShipmentData,
  getTransportersData,
} from "@/services/cache"
import type {
  ShipmentData,
  ShipmentRow,
  TransporterRow,
} from "@/services/cache"

type Transporter = TransporterRow & { hasActiveProfile?: boolean }

export type PetMarketplaceLocation =
  | { scope: "city"; city: string; stateAbbrev: string }
  | { scope: "state"; stateAbbrev: string }

export interface PetMarketplaceData {
  exportShipments: ShipmentRow[]
  importShipments: ShipmentRow[]
  transporters: Transporter[]
}

type ServerShipments = Partial<
  {
    [K in keyof ShipmentData]: ShipmentData[K] | null
  }
>

interface FetchedMarketplaceData {
  locationKey: string
  data: Partial<PetMarketplaceData>
}

const fetchMarketplaceData = async (
  scope: PetMarketplaceLocation["scope"],
  city: string,
  stateAbbrev: string,
): Promise<Partial<PetMarketplaceData>> => {
  if (scope === "state") {
    const state = stateAbbrev.toLowerCase()
    const [shipments, transporters] = await Promise.all([
      getStateShipmentData(state),
      getTransportersData("state", state),
    ])

    return { ...shipments, transporters }
  }

  const encodedCity = encodeURIComponent(city.toLowerCase())
  const [shipments, transporters] = await Promise.all([
    getCityShipmentData(encodedCity, stateAbbrev),
    getTransportersData("city", encodedCity, stateAbbrev),
  ])

  return { ...shipments, transporters }
}

const pickList = <T>(
  serverList: T[] | null | undefined,
  fetchedList: T[] | undefined,
): T[] => {
  if (serverList?.length) {
    return serverList
  }

  return fetchedList ?? []
}

/**
 * Marketplace lists for a pet location page: the server-rendered lists, with
 * any list the server returned empty filled by a client-side refetch.
 *
 * Next keeps the page component mounted when a visitor follows a link to
 * another location on the same route. Fetched lists are therefore tagged with
 * the location they were requested for and never shown on another one, and a
 * response that lands after the visitor has moved on is dropped.
 */
export function usePetMarketplaceData(
  location: PetMarketplaceLocation,
  serverShipments: ServerShipments | null | undefined,
  serverTransporters: Transporter[] | null | undefined,
): PetMarketplaceData {
  const { scope, stateAbbrev } = location
  const city = location.scope === "city" ? location.city : ""
  const locationKey = `${scope}:${stateAbbrev}:${city}`.toLowerCase()
  const [fetched, setFetched] = useState<FetchedMarketplaceData | null>(null)
  const hasEmptyServerList =
    !serverShipments?.exportShipments?.length ||
    !serverShipments?.importShipments?.length ||
    !serverTransporters?.length

  useEffect(() => {
    if (!hasEmptyServerList || !stateAbbrev) {
      return
    }

    let isCurrentRequest = true

    fetchMarketplaceData(scope, city, stateAbbrev)
      .then((data) => {
        if (isCurrentRequest) {
          setFetched({ locationKey, data })
        }
      })
      .catch((error) => {
        console.error("Client-side fetch failed:", error)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [hasEmptyServerList, locationKey, scope, city, stateAbbrev])

  const fetchedForLocation =
    fetched?.locationKey === locationKey ? fetched.data : undefined

  return {
    exportShipments: pickList(
      serverShipments?.exportShipments,
      fetchedForLocation?.exportShipments,
    ),
    importShipments: pickList(
      serverShipments?.importShipments,
      fetchedForLocation?.importShipments,
    ),
    transporters: pickList(
      serverTransporters,
      fetchedForLocation?.transporters,
    ),
  }
}
